import React from 'react';
import { Metrics } from '../types';
import { fmtEur } from '../utils/formatters';
import { RefreshCw, Shuffle } from 'lucide-react';

interface Props {
  metrics: Metrics;
  isDirty: boolean;
  seed: number;
  onRecompute: () => void;
  onNewScenario: () => void;
}

export const MetricsPanel: React.FC<Props> = ({
  metrics,
  isDirty,
  onRecompute,
  onNewScenario,
}) => {
  return (
    <div className="bg-white rounded-lg shadow-md border border-gray-200 p-4">
      <div className="flex justify-between items-center mb-3">
        <h2 className="text-base font-bold text-gray-800">Region Summary</h2>
        <div className="flex gap-1.5">
          <button
            onClick={onRecompute}
            disabled={!isDirty}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded transition-all ${
              isDirty
                ? 'bg-orange-500 text-white hover:bg-orange-600'
                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
            }`}
          >
            <RefreshCw className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">RECOMPUTE</span>
          </button>
          <button
            onClick={onNewScenario}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-blue-600 text-white rounded hover:bg-blue-700 transition-all"
          >
            <Shuffle className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">NEW</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <MetricCard
          label="Revenue"
          value={`€${fmtEur(metrics.totalEcon)}`}
          subtext="per year"
          color="text-green-600"
          bgColor="from-green-50 to-emerald-50"
        />
        <MetricCard
          label="GHG Balance"
          value={`${metrics.totalCarbon >= 0 ? '+' : ''}${metrics.totalCarbon.toFixed(1)}`}
          unit="tCO₂e"
          subtext={metrics.totalCarbon >= 0 ? 'source' : 'sink'}
          color={metrics.totalCarbon < 0 ? 'text-green-600' : 'text-red-600'}
          bgColor={metrics.totalCarbon < 0 ? 'from-green-50 to-emerald-50' : 'from-red-50 to-orange-50'}
        />
        <MetricCard
          label="Biodiversity"
          value={`${metrics.avgBio.toFixed(1)}%`}
          subtext="area avg"
          color="text-purple-600"
          bgColor="from-purple-50 to-pink-50"
        />
        <MetricCard
          label="Area"
          value={`${metrics.totalArea.toFixed(1)}`}
          unit="ha"
          subtext="of 400 ha"
          color="text-blue-600"
          bgColor="from-blue-50 to-cyan-50"
        />
      </div>
    </div>
  );
};

const MetricCard: React.FC<{ 
  label: string; 
  value: string;
  unit?: string;
  subtext?: string; 
  color?: string;
  bgColor?: string;
}> = ({ label, value, unit, subtext, color = 'text-gray-900', bgColor = 'from-gray-50 to-slate-50' }) => (
  <div className={`bg-gradient-to-br ${bgColor} border border-gray-200 rounded p-2.5`}>
    <div className="text-[9px] text-gray-600 uppercase tracking-wide font-semibold mb-0.5">
      {label}
    </div>
    <div className={`text-base font-bold font-mono ${color} leading-tight`}>
      {value}{unit && <span className="text-xs ml-0.5">{unit}</span>}
    </div>
    {subtext && <div className="text-[9px] text-gray-500 mt-0.5">{subtext}</div>}
  </div>
);